import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { whatsappTemplatesAPI } from '../services/api';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Imagen opcional de una plantilla de WhatsApp. Se envía por Baileys con el texto como pie de foto.
 * @param {{ plantilla?: { id: number, tiene_imagen?: number|boolean, imagen_filename?: string|null } | null,
 *           onChange?: () => void, useBaileys?: boolean, canEdit?: boolean }} props
 */
export function PlantillaImagenEditor({ plantilla, onChange, useBaileys = true, canEdit = true }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const plantillaId = plantilla?.id || null;
  const tieneImagen = !!(plantilla && Number(plantilla.tiene_imagen));
  const imagenKey = plantilla?.imagen_filename || '';

  useEffect(() => {
    let url = null;
    let cancelled = false;
    setPreviewUrl(null);
    if (plantillaId && tieneImagen) {
      whatsappTemplatesAPI.getImagenBlobUrl(plantillaId).then((u) => {
        if (cancelled) {
          if (u) URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setPreviewUrl(u);
      });
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [plantillaId, tieneImagen, imagenKey]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !plantillaId) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error('Formato no permitido. Use PNG, JPG o WEBP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('La imagen no puede superar 5 MB');
      return;
    }
    setBusy(true);
    try {
      await whatsappTemplatesAPI.uploadImagen(plantillaId, file);
      toast.success('Imagen guardada en la plantilla');
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'No se pudo subir la imagen');
    } finally {
      setBusy(false);
    }
  };

  const quitar = async () => {
    if (!plantillaId || !window.confirm('¿Quitar la imagen de esta plantilla?')) return;
    setBusy(true);
    try {
      await whatsappTemplatesAPI.deleteImagen(plantillaId);
      toast.success('Imagen quitada');
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'No se pudo quitar la imagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      {previewUrl && (
        <img
          src={previewUrl}
          alt="Imagen de la plantilla"
          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }}
        />
      )}
      {canEdit && (
        <>
          <input ref={inputRef} type="file" accept={ACCEPT} onChange={onFile} style={{ display: 'none' }} />
          <button
            type="button"
            className="btn-edit"
            onClick={() => inputRef.current?.click()}
            disabled={!plantillaId || busy}
            title={!plantillaId ? 'Guarde la plantilla antes de agregar una imagen' : 'PNG, JPG o WEBP, hasta 5 MB'}
          >
            {busy ? 'Procesando…' : tieneImagen ? 'Cambiar imagen' : 'Agregar imagen'}
          </button>
          {tieneImagen && (
            <button type="button" className="btn-delete" onClick={quitar} disabled={busy}>
              Quitar imagen
            </button>
          )}
        </>
      )}
      {!plantillaId && canEdit && (
        <span style={{ fontSize: 12, color: '#888' }}>Guarde o seleccione una plantilla para adjuntar una imagen.</span>
      )}
      {tieneImagen && !useBaileys && (
        <span style={{ fontSize: 12, color: '#b26a00' }}>
          La imagen solo se envía con el servicio Baileys; con wa.me se abre únicamente el texto.
        </span>
      )}
    </div>
  );
}
